/**
 * Organization Store
 *
 * Provides the current organization context to all components.
 * The server filters organizations by workspace (via the elauth_workspace_id cookie),
 * so the client simply fetches and uses whatever org belongs to the current workspace.
 *
 * To prevent stale org IDs from a previous workspace from leaking into
 * downstream stores (LedgerProvider, SetupGuard), we:
 *   1. Track which workspace the stored org ID was verified against.
 *   2. On mount, compare the current workspace cookie with the stored workspace.
 *   3. If they differ, clear the org ID immediately and re-resolve from server.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

import { v4 as uuidv4 } from 'uuid'

import { listOrganizations } from '../services/organization-service'

const ORG_STORAGE_KEY = 'elaccounts:current-organization-id'
const WS_STORAGE_KEY = 'elaccounts:current-workspace-id'

interface OrganizationContextType {
  organizationId: string
  setOrganizationId: (id: string) => void
  createNewOrganization: () => string
  isInitializing: boolean
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

interface OrganizationProviderProps {
  children: ReactNode
}

/** Read the elauth workspace cookie */
function getWorkspaceCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)elauth_workspace_id=([^;]*)/)
  return match?.[1] ? decodeURIComponent(match[1]) : ''
}

/**
 * Return the stored org ID only if it belongs to the current workspace.
 * If the workspace has changed since the org was stored, return '' so
 * downstream consumers don't start loading for the wrong org.
 */
function getVerifiedOrganizationId(): string {
  const storedOrgId = localStorage.getItem(ORG_STORAGE_KEY) ?? ''
  const storedWsId = localStorage.getItem(WS_STORAGE_KEY) ?? ''
  const currentWsId = getWorkspaceCookie()

  if (storedOrgId && storedWsId && storedWsId === currentWsId) {
    return storedOrgId
  }
  return ''
}

async function resolveOrgFromServer(
  setOrganizationIdState: (id: string) => void,
  setIsInitializing: (v: boolean) => void
): Promise<void> {
  try {
    const resp = await listOrganizations()
    if (resp.error) { setIsInitializing(false); return }
    const currentWsId = getWorkspaceCookie()
    if (!resp.data?.organizations?.length) {
      localStorage.removeItem(ORG_STORAGE_KEY)
      localStorage.removeItem(WS_STORAGE_KEY)
      setOrganizationIdState('')
      setIsInitializing(false)
      return
    }
    const org = resp.data.organizations[0]
    if (!org) { setIsInitializing(false); return }
    localStorage.setItem(ORG_STORAGE_KEY, org.id)
    localStorage.setItem(WS_STORAGE_KEY, currentWsId)
    setOrganizationIdState(org.id)
  } catch {
    // silently ignore server resolution failure
  } finally {
    setIsInitializing(false)
  }
}

export function OrganizationProvider({ children }: OrganizationProviderProps): JSX.Element {
  const [organizationId, setOrganizationIdState] = useState<string>(getVerifiedOrganizationId)
  const [isInitializing, setIsInitializing] = useState<boolean>(() => !getVerifiedOrganizationId())

  const resolveFromServer = useCallback(
    () => resolveOrgFromServer(setOrganizationIdState, setIsInitializing),
    []
  )

  useEffect(() => {
    // Always resolve from server to ensure we have the right org for the current workspace.
    // The stored ID might be from a different workspace session.
    void resolveFromServer()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist org ID whenever it changes
  useEffect(() => {
    if (organizationId) {
      localStorage.setItem(ORG_STORAGE_KEY, organizationId)
      // Also persist the workspace so future loads can verify
      const wsId = getWorkspaceCookie()
      if (wsId) {localStorage.setItem(WS_STORAGE_KEY, wsId)}
    }
  }, [organizationId])

  const setOrganizationId = (id: string): void => {
    setOrganizationIdState(id)
  }

  const createNewOrganization = (): string => {
    const newId = uuidv4()
    setOrganizationIdState(newId)
    return newId
  }

  return (
    <OrganizationContext.Provider
      value={{
        organizationId,
        setOrganizationId,
        createNewOrganization,
        isInitializing,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}

/**
 * Hook to access the current organization context
 */
export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}

/**
 * Get the current organization ID synchronously (for use outside React components)
 */
export function getCurrentOrganizationId(): string {
  return localStorage.getItem(ORG_STORAGE_KEY) ?? ''
}

/**
 * Persist an organization ID (for use outside React components).
 */
export function setCurrentOrganizationId(id: string): void {
  localStorage.setItem(ORG_STORAGE_KEY, id)
}

/**
 * Clear the persisted organization ID.
 */
export function clearCurrentOrganizationId(): void {
  localStorage.removeItem(ORG_STORAGE_KEY)
  localStorage.removeItem(WS_STORAGE_KEY)
}
